import React, { useCallback, useEffect, useMemo, useState, useContext, } from "react";
import { useParams } from "react-router-dom";
import { LassieContext } from "../../../context/LassieProvider";
import { toPrettyDateStr, formatInspectionDateToYMD } from "../../../prettyDate";
import { icons_at_risk, icons_handled, icons_marked_for_inspection } from "../../../components/ol-map/icons";
import WplSwitch from "../../../components/wpl-switch/wplSwitch";
import TurbineManagerPopup from "../../../components/turbine-namager-popup/TurbineManagerPopup";
import TurbineManagerTable from "../../../components/turbine-manager-table/TurbineMagerTable";
import TurbineAnimation from "../../../components/turbine-animation/TurbineAnimation";
import AdvanceFilters from "../../../components/advance-filters/AdvanceFilters";
import magnifierIcon from "../../../assets/img/magnifier.png";
import closeIcon from "../../../assets/img/cancel.png";
import closeIconW from "../../../assets/img/close-w.png";
import turbineIcon from "../../../assets/img/windfarm.png";
import WPLAnimatedInput from "../../../components/wpl-animated-input/WplAnimatedInput";
import "./turbine-manager.css";

export default function TurbineManager() {
  const { fetchTurbines, turbines, fetchOrganizations, selectedTurbineIds, setSelectedTurbineIds, mapPopUpLoading, turbinesLoading } = useContext(LassieContext);
  const [seeMoreTurbineIds, setSeeMoreTurbineIds] = useState([]);
  const [showUserActions, setShowUserActions] = useState(false);
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [filterOnPeak, setFilterOnPeak] = useState(true);
  const [onlyMostProbable, setOnlyMostProbable] = useState(false);
  const [onlyWithoutComment, setOnlyWithoutComment] = useState(false);
  const [peakCurrentRange, setPeakCurrentRange] = useState([-999, 999]);
  const [modelFilter, setModelFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState([]);
  const [proximityFilter, setProximityFilter] = useState(2000);
  const [probabilityFilter, setProbabilityFilter] = useState(null);
  const [showOutsideIEC, setShowOutsideIEC] = useState(false);
  const [sortField, setSortField] = useState("probability");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [isFiltersActive, setIsFilterActive] = useState(false);
  const [showSelectColumns, setShowSelectColumns] = useState(false)
  const allColumns = [
    { id: "name", label: "Name", sortable: true },
    { id: "status", label: "Status", sortable: false },
    { id: "distance", label: "Distance", sortable: true },
    { id: "probability", label: "Probability", sortable: true },
    { id: "outside_iec", label: "Outside IEC", sortable: true },
    { id: "model", label: "Model", sortable: true },
    { id: "comment", label: "Comment", sortable: false },
    { id: "inspection_type", label: "Inspection type", sortable: true },
    { id: "inspection_method", label: "Inspection method", sortable: true },
    { id: "due_date", label: "Inspection due date", sortable: true },
    { id: "date", label: "Inspection date", sortable: true },
    { id: "marked_for_repair", label: "Marked for repair", sortable: true },
    { id: "overdue_remind_in_days", label: "Overdue reminder", sortable: true }
  ];

  const [visibleColumnIds, setVisibleColumnIds] = useState([
    "name",
    "status",
    "distance",
    "probability",
    "outside_iec",
    "model",
    "comment",
  ]);

  const visibleColumns = allColumns.filter(col =>
    visibleColumnIds.includes(col.id)
  );

  let { windfarm_id } = useParams();
  if (windfarm_id) {
    windfarm_id = parseInt(windfarm_id);
  }

  useEffect(() => {
    if (!windfarm_id) return;
    const alreadyHasTurbinesFromThisWindfarm = turbines.length > 0 &&
      turbines.every((t) => t.windfarm_id === windfarm_id);
    if (!alreadyHasTurbinesFromThisWindfarm) {
      fetchTurbines(windfarm_id);
      fetchOrganizations();
    }
  }, [windfarm_id]);


  const sortTurbines = useCallback(
    (a, b) => {
      const sanitizeString = (value) => {
        if (value === null || value === undefined) return null;
        const str = String(value).trim();
        return str === '' ? null : str;
      };

      const compareBooleans = (rawA, rawB) => {
        const a = (rawA === null || rawA === undefined) ? null : rawA
        const b = (rawB === null || rawB === undefined) ? null : rawB

        return compareNullable(rawA, rawB, (left, right) => Number(left) - Number(right));
      };


      const compareNullable = (rawA, rawB, comparator) => {
        const isNil = (value) => value === null || value === undefined;

        const aIsNil = isNil(rawA);
        const bIsNil = isNil(rawB);

        if (aIsNil && bIsNil) return 0;
        if (aIsNil) return 1;
        if (bIsNil) return -1;

        const comparison = comparator(rawA, rawB);
        if (comparison === 0) return 0;

        return sortOrder === "ASC" ? comparison : -comparison;
      };

      const compareStrings = (rawA, rawB, options = {}) => {
        const { caseInsensitive = true } = options;

        const normalizedA = sanitizeString(rawA);
        const normalizedB = sanitizeString(rawB);

        const comparator = (left, right) => {
          const lhs = caseInsensitive ? left.toLowerCase() : left;
          const rhs = caseInsensitive ? right.toLowerCase() : right;
          return lhs.localeCompare(rhs);
        };

        return compareNullable(normalizedA, normalizedB, comparator);
      };

      const compareNumbers = (rawA, rawB) => {
        const toNumber = (value) => {
          if (typeof value !== 'number' || Number.isNaN(value)) return null;
          return value;
        };

        return compareNullable(toNumber(rawA), toNumber(rawB), (left, right) => left - right);
      };

      const compareDates = (rawA, rawB) => {
        const toTimestamp = (value) => {
          if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;
          return value.getTime();
        };

        return compareNumbers(toTimestamp(rawA), toTimestamp(rawB));
      };

      const naturalCompare = (rawA, rawB) => {
        const comparator = (left, right) => {
          const regex = /(\d+)|(\D+)/g;
          const leftChunks = left.match(regex) || [left];
          const rightChunks = right.match(regex) || [right];

          for (let i = 0; i < Math.min(leftChunks.length, rightChunks.length); i += 1) {
            const leftChunk = leftChunks[i];
            const rightChunk = rightChunks[i];

            const leftIsNumber = !Number.isNaN(Number(leftChunk));
            const rightIsNumber = !Number.isNaN(Number(rightChunk));

            if (leftIsNumber && rightIsNumber) {
              const diff = Number(leftChunk) - Number(rightChunk);
              if (diff !== 0) return diff;
            } else if (leftChunk !== rightChunk) {
              return leftChunk.localeCompare(rightChunk);
            }
          }

          return leftChunks.length - rightChunks.length;
        };

        return compareNullable(sanitizeString(rawA), sanitizeString(rawB), comparator);
      };

      const probabilityValue = (value) => {
        if (typeof value !== 'number' || Number.isNaN(value)) return null;
        if (Math.round(value * 100) === 0) return null;
        return value;
      };

      let result = 0;

      switch (sortField) {
        case 'name':
          result = naturalCompare(a.name, b.name);
          break;

        case 'distance': {
          const minDistance = (turbine) => {
            const distances = (turbine.turbine_lightning || [])
              .map((lightning) => lightning.distance_km)
              .filter((distance) => typeof distance === 'number' && !Number.isNaN(distance));

            if (distances.length === 0) return null;
            return Math.min(...distances);
          };

          result = compareNumbers(minDistance(a), minDistance(b));
          break;
        }

        case 'due_date':
        case 'date': {
          const inspectionDate = (turbine) => {
            const rawDate = turbine.inspection?.[sortField];
            return rawDate ? new Date(rawDate) : null;
          };

          result = compareDates(inspectionDate(a), inspectionDate(b));
          break;
        }

        case 'inspection_type':
          result = compareStrings(a.inspection?.type, b.inspection?.type);
          break;

        case 'inspection_method':
          result = compareStrings(a.inspection?.method, b.inspection?.method);
          break;

        case 'inspection_status':
          result = compareStrings(a.inspection?.status, b.inspection?.status);
          break;
        case 'marked_for_repair':
          result = compareNullable(a.marked_for_repair, b.marked_for_repair, (a, b) => {
            return Number(a) - Number(b);
          });
          break;
        case 'overdue_remind_in_days': {
          const reminderTimestamp = (turbine) => {
            const dueDate = turbine.inspection?.due_date ? new Date(turbine.inspection.due_date) : null;
            const remindDays = turbine.inspection?.overdue_remind_in_days;

            if (!dueDate || typeof remindDays !== 'number') return null;

            const reminderDate = addDays(dueDate, remindDays);
            return reminderDate instanceof Date && !Number.isNaN(reminderDate)
              ? reminderDate.getTime()
              : null;
          };

          result = compareNumbers(reminderTimestamp(a), reminderTimestamp(b));
          break;
        }
        case 'outside_iec': {
          const hasOutsideIec = (turbine) =>
            Array.isArray(turbine.turbine_lightning) &&
            turbine.turbine_lightning.some((tl) => tl.outside_certification);

          result = compareBooleans(hasOutsideIec(a), hasOutsideIec(b));
          break;
        }
        case 'model':
          result = compareStrings(a.model, b.model);
          break;

        case 'probability':
        default: {
          const primary = compareNumbers(
            probabilityValue(a.probability_of_atleast_one),
            probabilityValue(b.probability_of_atleast_one)
          );

          if (primary !== 0) {
            result = primary;
            break;
          }

          const maxProbability = (turbine) => {
            if (!Array.isArray(turbine.turbine_lightning) || turbine.turbine_lightning.length === 0) {
              return null;
            }

            const probabilities = turbine.turbine_lightning
              .map((lightning) => probabilityValue(lightning.probability_of_hit))
              .filter((probability) => probability !== null);

            if (probabilities.length === 0) return null;
            return Math.max(...probabilities);
          };

          result = compareNumbers(maxProbability(a), maxProbability(b));
          break;
        }
      }

      return result;
    },
    [sortField, sortOrder]
  );
  const filteredTurbines = useMemo(() => {
    if (!turbines) return [];
    let res = [...turbines];

    res = res
      .filter(
        (t) =>
          !searchText ||
          t.name.toLowerCase().indexOf(searchText.toLowerCase()) >= 0
      )
      .filter((t) => {
        if (!statusFilter.length) return true;

        let actionStatus;

        if (t.marked_for_repair) {
          actionStatus = "marked_for_inspection";


        } else {
          const inspectionStatus = t.inspection?.status;

          if (inspectionStatus === "pending") {
            actionStatus = "at_risk";
          } else if (
            inspectionStatus === "scheduled" ||
            inspectionStatus === "disposition_ready"
          ) {
            actionStatus = "marked_for_inspection";
          } else if (
            inspectionStatus === "completed" ||
            inspectionStatus === "rejected"
          ) {
            actionStatus = "acceptable_risk";
          } else {
            actionStatus = t.status;
          }
        }
        return statusFilter.includes(actionStatus);
      })


      .filter(
        (t) =>
          !showOutsideIEC ||
          t.turbine_lightning.some((tl) => tl.outside_certification)
      )
      .filter((t) => {
        if (isFiltersActive) {
          return (
            !modelFilter ||
            modelFilter === "All Models" ||
            t.model === modelFilter
          );
        }
        return true;
      })
      .filter((t) => {
        if (isFiltersActive) {
          return (
            probabilityFilter === null ||
            Math.round(t.probability_of_atleast_one * 100) >= probabilityFilter
          );
        }
        return true;
      });

    res = res
      .map((t) => {
        const filtered_lightning = t.turbine_lightning
          .filter((l) => !onlyMostProbable || l.most_probable)
          .filter((l) => {
            if (isFiltersActive) {
              return (
                l.peak_current > peakCurrentRange[0] &&
                l.peak_current < peakCurrentRange[1]
              );
            }
            return true;
          })
          .filter((l) => {
            if (isFiltersActive) {
              const distanceInMeters = l.distance_km * 1000;
              return distanceInMeters < proximityFilter;
            }
            return true;
          })
          .filter((l) => {
            if (isFiltersActive) {
              return (
                probabilityFilter === null ||
                Math.round(l.probability_of_hit * 100) >= probabilityFilter
              );
            }
            return true;
          })
          .filter((l) => !showOutsideIEC || l.outside_certification);

        if (
          filtered_lightning.length === 0 &&
          (isFiltersActive || onlyMostProbable)
        ) {
          return null;
        }

        const prob =
          1 -
          filtered_lightning
            .map((tl) => 1 - tl.probability_of_hit)
            .reduce((p, c) => p * c, 1);

        return {
          ...t,
          turbine_lightning: filtered_lightning,
          probability_of_atleast_one: prob,
        };
      })
      .filter((t) => t !== null);

    return res;
  }, [
    turbines,
    modelFilter,
    onlyWithoutComment,
    statusFilter,
    probabilityFilter,
    showOutsideIEC,
    proximityFilter,
    filterOnPeak,
    onlyMostProbable,
    peakCurrentRange,
    isFiltersActive,
    searchText
  ]);

  const modelOptions = useMemo(() => {
    const models = turbines.map((t) => t.model);
    const uniqueModels = models.filter((m, idx) => models.indexOf(m) === idx);
    return ["All Models", ...uniqueModels];
  }, [turbines]);

  const columnValueMap = {
    name: (t) => t.name,
    status: (t) => t.status,
    distance: (t) =>
      Math.round(
        Math.min(...t.turbine_lightning.map((l) => parseFloat(l.distance_km))) * 1000
      ),
    probability: (t) => Math.round(t.probability_of_atleast_one * 100),
    outside_iec: (t) =>
      t.turbine_lightning.some((tl) => tl.outside_certification)
        ? "Outside certification"
        : "",
    model: (t) => t.model,
    comment: (t) => t.user_comment,
    inspection_type: (t) => t.inspection?.type ?? "",
    inspection_method: (t) => t.inspection?.method ?? "",
    due_date: (t) => formatInspectionDateToYMD(t.inspection?.due_date) ?? "",
    date: (t) => t.inspection?.date ?? "",
    inspection_status: (t) => t.inspection?.status ?? "",
    marked_for_repair: (t) => t.marked_for_repair ? "Yes" : "No",
    overdue_remind_in_days: (t) => {
      const inspectionDate = t.inspection?.due_date ? new Date(t.inspection.due_date) : null;
      const remindDays = t.inspection?.overdue_remind_in_days;

      if (!inspectionDate || typeof remindDays !== "number") return "";

      const reminderDate = addDays(inspectionDate, remindDays);
      const now = new Date();
      const daysRemaining = Math.ceil((reminderDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      return daysRemaining > 0
        ? `In ${daysRemaining} day${daysRemaining !== 1 ? "s" : ""}`
        : `Sent ${toPrettyDateStr(reminderDate)}`;
    },
  };

  const downloadWorkOrderCSV = () => {
    const exportTurbines = filteredTurbines
      .filter((t) => selectedTurbineIds.some((sel) => sel.id === t.id))
      .map((t) => {
        const row = {};
        visibleColumnIds.forEach((colId) => {
          const valueGetter = columnValueMap[colId];
          row[colId] = valueGetter ? valueGetter(t) : "";
        });
        return row;
      });

    if (exportTurbines.length === 0) return;

    const csvData =
      "data:text/csv;charset=utf-8," +
      visibleColumnIds.map((colId) => allColumns.find((c) => c.id === colId)?.label ?? colId)
        .join(",") + "\n" +
      exportTurbines
        .map((row) => visibleColumnIds.map((colId) => row[colId]).join(","))
        .join("\n");

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvData));
    link.setAttribute(
      "download",
      `${toPrettyDateStr(new Date())}-lassie-work-order.csv`
    );
    document.body.appendChild(link);
    link.click();
  };

  const handleMinChange = (e) => {
    const newMin = e.target.value;
    if (newMin === "" || !isNaN(newMin)) {
      setPeakCurrentRange((prevRange) => [
        newMin === "" ? "" : Number(newMin),
        prevRange[1],
      ]);
    }
  };

  const handleMaxChange = (e) => {
    const newMax = e.target.value;
    if (newMax === "" || !isNaN(newMax)) {
      setPeakCurrentRange((prevRange) => [
        prevRange[0],
        newMax === "" ? "" : Number(newMax),
      ]);
    }
  };

  const handleDistanceChange = (e) => {
    const newDistance = e.target.value;
    if (newDistance === "" || !isNaN(newDistance)) {
      setProximityFilter(newDistance === "" ? "" : Number(newDistance));
    }
  };

  const handleProbabilityChange = (e) => {
    const newProbability = e.target.value;
    if (newProbability === "" || !isNaN(newProbability)) {
      setProbabilityFilter(newProbability === "" ? "" : Number(newProbability));
    }
  };

  const removeTurbine = (id) => {
    setSelectedTurbineIds((prev) =>
      prev.filter((sel) => sel.id !== id)
    );
  };


  const toggleStatusFilter = (status) => {
    setStatusFilter((prevFilter) =>
      prevFilter.includes(status)
        ? prevFilter.filter((s) => s !== status)
        : [...prevFilter, status]
    );
  };

  const renderManagedTurbines = (t, content, className, key) => {
    return t.turbine_lightning.length !== 0 ? (
      <td key={key} className={className}>{content}</td>
    ) : (
      <td key={key}>N/A</td>
    );
  };


  const handleRowCheckboxChange = (e, turbineId, index) => {
    const isShiftPressed = e.nativeEvent.shiftKey;
    const isSelected = selectedTurbineIds.some(t => t.id === turbineId);


    if (isShiftPressed && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const range = filteredTurbines.slice(start, end + 1).map((t) => ({
        id: t.id,
        inspection_id: t.inspection?.id ?? null,
      }));

      if (isSelected) {
        setSelectedTurbineIds((prev) =>
          prev.filter((t) => !range.some(r => r.id === t.id))
        );
      } else {
        setSelectedTurbineIds((prev) =>
          [...prev, ...range.filter(r => !prev.some(p => p.id === r.id))]
        );
      }
    } else {
      if (isSelected) {
        setSelectedTurbineIds((prev) => prev.filter((t) => t.id !== turbineId));
      } else {
        const inspectionId = filteredTurbines[index]?.inspection?.id ?? null;
        setSelectedTurbineIds((prev) => [...prev, { id: turbineId, inspection_id: inspectionId }]);
      }
    }
    setLastSelectedIndex(index);
  };

  const handleHeaderCheckboxChange = () => {
    const allFilteredSelected = filteredTurbines.every((ft) =>
      selectedTurbineIds.some((sel) => sel.id === ft.id)
    );

    if (allFilteredSelected) {
      setSelectedTurbineIds((prev) =>
        prev.filter((sel) => !filteredTurbines.some((ft) => ft.id === sel.id))
      );
    } else {
      const newSelections = filteredTurbines
        .filter((ft) => !selectedTurbineIds.some((sel) => sel.id === ft.id))
        .map((t) => ({
          id: t.id,
          inspection_id: t.inspection?.id ?? null,
        }));

      setSelectedTurbineIds((prev) => [...prev, ...newSelections]);
    }
  };

  const resetFilters = () => {
    setFilterOnPeak(true);
    setOnlyMostProbable(false);
    setOnlyWithoutComment(false);
    setPeakCurrentRange([-999, 999]);
    setModelFilter(null);
    setStatusFilter([]);
    setProximityFilter(2000);
    setProbabilityFilter(null);
    setShowOutsideIEC(false);
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prevOrder) => (prevOrder === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortField(field);
      setSortOrder("ASC");
    }
  };

  const resetColumns = () => {
    setVisibleColumnIds(([
      "name",
      "status",
      "distance",
      "probability",
      "outside_iec",
      "model",
      "comment",
    ]))
  }

  const addDays = (date, days) => {
    if (!date || isNaN(days)) return null;
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  const toggleColumnVisibility = (columnId, isVisible, setVisibleColumnIds) => {
    if (columnId === "name" && isVisible) return;
    setVisibleColumnIds((prev) => {
      if (isVisible) {
        return prev.filter((id) => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  return (
    <div className="turbine-manager">

      {
        (mapPopUpLoading || turbinesLoading) &&
        <TurbineAnimation overlayClassname="turbine-manager-overlay" />
      }

      {showUserActions && (
        <TurbineManagerPopup
          showPopup={showUserActions}
          closePopup={setShowUserActions}
          filteredTurbines={filteredTurbines}
          removeTurbine={removeTurbine}
        >
        </TurbineManagerPopup>
      )}
      <div className="filter-menu">
        <div className="turbine-status-container">
          <div className="search-bar-container">
            <div className="search-bar-wrapper">
              <WPLAnimatedInput
                label="Search turbines..."
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                inputClassName="search-input"
                containerClassName="search-input-container"
              />
              <img src={magnifierIcon} className="search-icon" alt="Search" />
            </div>
            <div className="count-display">
              <p className="label">
                <img
                  src={turbineIcon}
                  className="turbine-icon"
                  alt="turbine icon"
                />
                {filteredTurbines.length}/{turbines.length}
              </p>
            </div>
          </div>
          <div>
            <h4 className="label">Turbine status</h4>
          </div>
          <div className="status-btn-container">
            <div
              className={`status-btn ${statusFilter.includes("at_risk") ? "status-active" : ""
                }`}
              onClick={() => toggleStatusFilter("at_risk")}
            >
              <div className="button__content">
                <img
                  src={icons_at_risk.High}
                  alt="At risk"
                  title="At risk"
                  className="status-icon"
                />
              </div>
            </div>

            <div
              className={`status-btn ${statusFilter.includes("marked_for_inspection")
                ? "status-active"
                : ""
                }`}
              onClick={() => toggleStatusFilter("marked_for_inspection")}
            >
              <div className="button__content">
                <img
                  src={icons_marked_for_inspection}
                  alt="Marked for inspection"
                  title="Marked for inspection"
                  className="status-icon"
                />
              </div>
            </div>

            <div
              className={`status-btn ${statusFilter.includes("acceptable_risk") ? "status-active" : ""
                }`}
              onClick={() => toggleStatusFilter("acceptable_risk")}
            >
              <div className="button__content">
                <img
                  src={icons_handled}
                  alt="Acceptable risk"
                  title="Acceptable risk"
                  className="status-icon"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="probable-strikes-container">
          <h4 className="label">Only show outside IEC</h4>
          <span className="switch-container">
            <WplSwitch
              value={showOutsideIEC}
              onChanged={() => setShowOutsideIEC(!showOutsideIEC)}
              tooltip="Read more about IEC certification in the FAQ"
            />
          </span>

          <h4 className="label">Probable strikes only </h4>

          <span className="switch-container">
            <WplSwitch
              value={onlyMostProbable}
              onChanged={() => setOnlyMostProbable(!onlyMostProbable)}
            />
          </span>
          <h4 className="label">Advance filters </h4>
          <span className="switch-container">
            <WplSwitch
              value={isFiltersActive}
              onChanged={() => {
                setIsFilterActive((prev) => {
                  const newValue = !prev;
                  if (newValue) setShowSelectColumns(false);
                  return newValue;
                });
              }}
            />
          </span>
          <h4 className="label">Customize table</h4>
          <span className="switch-container">
            <WplSwitch
              value={showSelectColumns}
              onChanged={() => {
                setShowSelectColumns((prev) => {
                  const newValue = !prev;
                  if (newValue) setIsFilterActive(false);
                  return newValue;
                });
              }} />
          </span>
        </div>

        {isFiltersActive && (
          <div className="advance-filters-container fade-slide-in">
            <AdvanceFilters
              showAdvanceFilter={isFiltersActive}
              modelFilter={modelFilter}
              setModelFilter={setModelFilter}
              resetFilters={resetFilters}
              modelOptions={modelOptions}
              showOutsideIEC={showOutsideIEC}
              setShowOutsideIEC={setShowOutsideIEC}
              onlyMostProbable={onlyMostProbable}
              setOnlyMostProbable={setOnlyMostProbable}
              peakCurrentRange={peakCurrentRange}
              handleMinChange={handleMinChange}
              handleMaxChange={handleMaxChange}
              proximityFilter={proximityFilter}
              handleDistanceChange={handleDistanceChange}
              probabilityFilter={probabilityFilter}
              handleProbabilityChange={handleProbabilityChange}
            />
          </div>
        )}

        {showSelectColumns && (
          <>
            <button onClick={() => resetColumns()} className="reset-columns-btn">Reset columns</button>
            <div className="column-selector-container fade-slide-in">
              {allColumns.map((column) => {
                const isVisible = visibleColumnIds.includes(column.id);
                if (column.id === "name") return
                return (
                  <div
                    key={column.id}
                    className={`column-badge ${isVisible ? "selected-column-badge" : "unselected-column-badge"}`}
                    onClick={() => toggleColumnVisibility(column.id, isVisible, setVisibleColumnIds)}

                  >
                    <p>{column.label}</p>
                  </div>
                );
              })}
            </div>
          </>
        )}


        <div>
          <h4 className="label">Selected turbines</h4>
          <div className="flex-center">
            <p className="label">
              {
                turbines.filter((t) =>
                  selectedTurbineIds.some((s) => s.id === t.id)
                ).length
              }{" "}
              selected
            </p>

            <div
              className="clear-badge"
              onClick={() => setSelectedTurbineIds([])}
            >
              <p className="turbine-name">Clear selection</p>
              <img src={closeIcon} className="clear-icon" alt="close icon" />
            </div>
          </div>
        </div>
        <div className="selected-turbines-container">

          {turbines
            .filter((t) => selectedTurbineIds.some((s) => s.id === t.id))
            .map((t, index, arr) => (
              <div
                key={t.id}
                className="turbine-name-badge"
                onClick={() => removeTurbine(t.id)}
              >
                <p className="turbine-name">{t.name}</p>
                <img
                  src={closeIconW}
                  className="delete-badge-icon"
                  alt="close icon"
                />
              </div>
            ))}

        </div>

        <div className="btn-container">
          <button
            className="sidebar-btn"
            onClick={() => setShowUserActions(true)}
            disabled={selectedTurbineIds.length === 0}
          >
            Action menu
          </button>
          <button
            className="sidebar-btn"
            onClick={downloadWorkOrderCSV}
            disabled={selectedTurbineIds.length === 0}
          >
            Export
          </button>
        </div>
      </div>

      <TurbineManagerTable
        filteredTurbines={filteredTurbines}
        handleHeaderCheckboxChange={handleHeaderCheckboxChange}
        handleRowCheckboxChange={handleRowCheckboxChange}
        toggleSort={toggleSort}
        sortField={sortField}
        sortOrder={sortOrder}
        sortTurbines={sortTurbines}
        seeMoreTurbineIds={seeMoreTurbineIds}
        setSeeMoreTurbineIds={setSeeMoreTurbineIds}
        renderManagedTurbines={renderManagedTurbines}
        allColumns={allColumns}
        visibleColumnIds={visibleColumnIds}
        setVisibleColumnIds={setVisibleColumnIds}
        visibleColumns={visibleColumns}
        addDays={addDays}
      />

    </div>
  );
}
